"use client";

import { doc, onSnapshot } from "firebase/firestore";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { FeatureFlagKey } from "shared";
import type {
  FeatureFlagValue,
  OrgFeatureFlag,
  SystemFeatureFlag,
} from "shared";
import {
  getAllFeatureFlagKeys,
  getFeatureFlagDefinition,
  resolveFeatureFlag,
} from "shared";

import { db } from "../firebase/clientApp";
import { useOrgUid } from "../hooks/useCurrentOrg";

/**
 * Feature Flags Context Type
 */
export interface FeatureFlagsContextType {
  flags: Partial<Record<FeatureFlagKey, FeatureFlagValue>>;
  loading: boolean; // true until we've received at least one response from Firestore
  error: Error | null;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

/**
 * Feature Flags Provider Component
 *
 * Provides real-time feature flag state to all child components.
 * Listens to both system-level and org-level feature flags and resolves
 * them according to priority: killSwitch > org > system > default
 *
 * @example
 * // In your root layout:
 * <FeatureFlagsProvider>
 *   <App />
 * </FeatureFlagsProvider>
 */
export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const orgUid = useOrgUid();
  const [systemFlags, setSystemFlags] = useState<
    Record<string, SystemFeatureFlag | null>
  >({});
  const [orgFlags, setOrgFlags] = useState<
    Record<string, OrgFeatureFlag | null>
  >({});
  const [error, setError] = useState<Error | null>(null);
  // Track which system flags have received their first response
  const [
    systemFlagsReceivedFirstResponse,
    setSystemFlagsReceivedFirstResponse,
  ] = useState<Set<string>>(new Set());

  // Delay listener initialization until after first render to reduce initial load
  const [isInitialized, setIsInitialized] = useState(false);

  // Get all defined feature flag keys
  const flagKeys = useMemo(() => getAllFeatureFlagKeys(), []);

  // Loading is true if we have flags but not all have received first response yet
  const loading =
    flagKeys.length > 0 &&
    systemFlagsReceivedFirstResponse.size < flagKeys.length;

  // Initialize listeners after first render to reduce initial load
  useEffect(() => {
    // Use setTimeout to defer initialization until after initial render
    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  // Set up real-time listeners for system-level flags
  // Only initialize after first render to reduce initial load
  useEffect(() => {
    if (!isInitialized) return;

    setSystemFlags({});
    setError(null);
    setSystemFlagsReceivedFirstResponse(new Set());

    const unsubscribes: (() => void)[] = [];

    flagKeys.forEach((flagKey) => {
      const systemFlagRef = doc(db, "featureFlags", flagKey);
      const unsubscribe = onSnapshot(
        systemFlagRef,
        (snapshot) => {
          setSystemFlags((prev) => {
            const updated = { ...prev };
            if (snapshot.exists()) {
              updated[flagKey] = snapshot.data() as SystemFeatureFlag;
            } else {
              updated[flagKey] = null;
            }
            return updated;
          });
          // Mark this flag as having received its first response
          setSystemFlagsReceivedFirstResponse((prev) => {
            if (!prev.has(flagKey)) {
              return new Set(prev).add(flagKey);
            }
            return prev;
          });
        },
        (err) => {
          // Permission denied errors are expected during initial load before auth
          // or when flags don't exist - only log non-permission errors
          const isPermissionError =
            (err as { code?: string })?.code === "permission-denied";
          if (!isPermissionError) {
            console.error(
              `[FeatureFlagsContext] Error listening to system flag ${flagKey}:`,
              err,
            );
            setError(err as Error);
          }
          setSystemFlags((prev) => {
            const updated = { ...prev };
            updated[flagKey] = null;
            return updated;
          });
          // Mark this flag as having received its first response (even if it's an error)
          setSystemFlagsReceivedFirstResponse((prev) => {
            if (!prev.has(flagKey)) {
              return new Set(prev).add(flagKey);
            }
            return prev;
          });
        },
      );
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [flagKeys, isInitialized]);

  // Set up real-time listeners for org-level flags
  // Only initialize after first render to reduce initial load
  useEffect(() => {
    if (!isInitialized || !orgUid || flagKeys.length === 0) {
      return;
    }

    const unsubscribes: (() => void)[] = [];

    flagKeys.forEach((flagKey) => {
      const orgFlagRef = doc(db, "orgs", orgUid, "featureFlags", flagKey);
      const unsubscribe = onSnapshot(
        orgFlagRef,
        (snapshot) => {
          setOrgFlags((prev) => {
            const updated = { ...prev };
            if (snapshot.exists()) {
              updated[flagKey] = snapshot.data() as OrgFeatureFlag;
            } else {
              updated[flagKey] = null;
            }
            return updated;
          });
        },
        (err) => {
          // Permission denied errors are expected when flags don't exist or before org access is granted
          // Only log non-permission errors for org flags since they're optional
          const isPermissionError =
            (err as { code?: string })?.code === "permission-denied";
          if (!isPermissionError) {
            console.error(
              `[FeatureFlagsContext] Error listening to org flag ${flagKey}:`,
              err,
            );
          }
          // Don't set error state for org flags - they're optional
        },
      );
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [orgUid, flagKeys, isInitialized]);

  // Resolve all flags (using defaults for flags that haven't loaded yet)
  // Return defaults immediately if not initialized, empty object while loading
  const resolvedFlags = useMemo(() => {
    // If not initialized yet, return defaults immediately (no listeners set up)
    if (!isInitialized) {
      const defaults: Partial<Record<FeatureFlagKey, FeatureFlagValue>> = {};
      flagKeys.forEach((flagKey) => {
        const definition = getFeatureFlagDefinition(flagKey);
        if (definition) {
          defaults[flagKey] = {
            enabled:
              typeof definition.defaultValue === "boolean"
                ? definition.defaultValue
                : true,
            value: definition.defaultValue,
            source: "default",
          };
        }
      });
      return defaults;
    }

    if (loading) {
      return {};
    }

    const resolved: Partial<Record<FeatureFlagKey, FeatureFlagValue>> = {};

    flagKeys.forEach((flagKey) => {
      const definition = getFeatureFlagDefinition(flagKey);
      if (!definition) {
        return;
      }

      // Use null if flag hasn't loaded yet, otherwise use the loaded value
      const systemFlag = systemFlags[flagKey] ?? null;
      const orgFlag = orgFlags[flagKey] ?? null;

      resolved[flagKey] = resolveFeatureFlag(
        flagKey,
        systemFlag,
        orgFlag,
        definition.defaultValue,
      );
    });

    return resolved;
  }, [isInitialized, loading, flagKeys, systemFlags, orgFlags]);

  const contextValue: FeatureFlagsContextType = {
    flags: resolvedFlags,
    loading,
    error,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access the feature flags context
 *
 * @throws Error if used outside of FeatureFlagsProvider
 *
 * @example
 * const { flags, loading, error } = useFeatureFlagsContext();
 */
export function useFeatureFlagsContext(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagsContext must be used within a FeatureFlagsProvider",
    );
  }
  return context;
}

/**
 * Hook to get a specific feature flag
 *
 * @param flagKey - The feature flag key
 * @returns The resolved feature flag value, or null if not found
 *
 * @example
 * const flag = useFeatureFlag(FEATURE_FLAG_KEYS.COMMUNICATIONS_SYSTEM);
 * if (flag?.enabled) {
 *   return <CommunicationsSystem />;
 * }
 */
export function useFeatureFlag(
  flagKey: FeatureFlagKey,
): FeatureFlagValue | null {
  const { flags } = useFeatureFlagsContext();
  return flags[flagKey] ?? null;
}

/**
 * Hook to get all feature flags
 *
 * @returns Partial record of all resolved feature flags
 *
 * @example
 * const flags = useFeatureFlags();
 * if (flags[FEATURE_FLAG_KEYS.COMMUNICATIONS_SYSTEM]?.enabled) {
 *   // ...
 * }
 */
export function useFeatureFlags(): Partial<
  Record<FeatureFlagKey, FeatureFlagValue>
> {
  const { flags } = useFeatureFlagsContext();
  return flags;
}
